import type { ReactElement } from "react";
import { Controller, useWatch, type Control, type FieldErrors, type UseFormRegister, type UseFormSetValue, type UseFormWatch } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Panel } from "primereact/panel";
import { Checkbox } from "primereact/checkbox";
import { AdminCalendar, FormField } from "@/components/forms";
import { formStyles } from "@/components/forms/formStyles";
import type { BookableEventFormData, NonBookableEventFormData } from "@/lib/event-schemas";

type BookableProps = {
  isBookable: true;
  register: UseFormRegister<BookableEventFormData>;
  errors: FieldErrors<BookableEventFormData>;
  control: Control<BookableEventFormData>;
};

type NonBookableProps = {
  isBookable: false;
  register: UseFormRegister<NonBookableEventFormData>;
  errors: FieldErrors<NonBookableEventFormData>;
  control: Control<NonBookableEventFormData>;
  watch: UseFormWatch<NonBookableEventFormData>;
  setValue: UseFormSetValue<NonBookableEventFormData>;
};

export function BasicInfoFields(props: BookableProps | NonBookableProps): ReactElement {
  const audienceOptions = [
    { label: "Open", value: "OPEN" },
    { label: "Members Only", value: "MEMBERS_ONLY" },
  ] as const;

  // Watch isRecurring (both BookableEventFormData and NonBookableEventFormData have this field)
  // We only use it for non-bookable events, but we need to call the hook unconditionally
  const isRecurring = Boolean(
    useWatch({ 
      control: props.control as Control<BookableEventFormData | NonBookableEventFormData>, 
      name: "isRecurring" as const 
    })
  );

  const eventKindOptions = [
    { label: "Standard", value: "STANDARD" },
    { label: "Race", value: "RACE" },
  ] as const;

  if (props.isBookable) {
    return (
      <Panel header="Basic Information">
        <div className="flex flex-column gap-2">
          <FormField
            id="eventKind"
            label="Event type"
            helperText="Standard events are for individual bookings. Race events use boats with crew; price points are boat categories (e.g. Single-handed, Double-handed)"
            error={props.errors.eventKind?.message}
          >
            <Controller
              control={props.control}
              name="eventKind"
              render={({ field }) => (
                <Dropdown
                  id="eventKind"
                  value={field.value}
                  options={[...eventKindOptions]}
                  onChange={(e) => field.onChange(e.value)}
                  className={`w-full ${props.errors.eventKind ? "p-invalid" : ""}`}
                />
              )}
            />
          </FormField>

          <FormField
            id="title"
            label="Title"
            required
            error={props.errors.title?.message}
          >
            <InputText
              id="title"
              {...props.register("title")}
              className={`w-full ${props.errors.title ? "p-invalid" : ""}`}
              style={{
                ...formStyles.input,
                ...(props.errors.title ? { borderColor: "#dc3545", borderWidth: "1px", borderStyle: "solid" } : {}),
              }}
            />
          </FormField>

          <FormField
            id="description"
            label="Description"
            required
            error={props.errors.description?.message}
          >
            <InputTextarea
              id="description"
              {...props.register("description")}
              rows={5}
              className={`w-full ${props.errors.description ? "p-invalid" : ""}`}
              style={{
                ...formStyles.input,
                ...(props.errors.description ? { borderColor: "#dc3545", borderWidth: "1px", borderStyle: "solid" } : {}),
              }}
            />
          </FormField>

          <FormField
            id="location"
            label="Location"
            optional
            helperText="Location of the event (e.g., Clubhouse, Main Dock, Training Room)"
            error={props.errors.location?.message}
          >
            <InputText
              id="location"
              {...props.register("location")}
              className={`w-full ${props.errors.location ? "p-invalid" : ""}`}
              placeholder="e.g., Clubhouse"
              style={formStyles.input}
            />
          </FormField>

          <FormField
            id="schedule"
            label="Schedule"
            optional
            helperText="Event schedule information"
            error={props.errors.schedule?.message}
          >
            <InputTextarea
              id="schedule"
              {...props.register("schedule")}
              rows={3}
              className={`w-full ${props.errors.schedule ? "p-invalid" : ""}`}
              placeholder="Event schedule information..."
              style={formStyles.input}
            />
          </FormField>

          <FormField
            id="whatToBring"
            label="What to Bring"
            optional
            helperText="What participants should bring to the event"
            error={props.errors.whatToBring?.message}
          >
            <InputTextarea
              id="whatToBring"
              {...props.register("whatToBring")}
              rows={3}
              className={`w-full ${props.errors.whatToBring ? "p-invalid" : ""}`}
              placeholder="What participants should bring..."
              style={formStyles.input}
            />
          </FormField>

          <FormField
            id="audience"
            label="Audience"
            error={props.errors.audience?.message}
          >
            <Controller
              control={props.control}
              name="audience"
              render={({ field }) => (
                <Dropdown
                  id="audience"
                  value={field.value}
                  options={[...audienceOptions]}
                  onChange={(e) => field.onChange(e.value)}
                  className={`w-full ${props.errors.audience ? "p-invalid" : ""}`}
                />
              )}
            />
          </FormField>

          <FormField
            id="isFeatured"
            label="Featured Event"
            optional
            helperText="Featured events appear on the homepage"
          >
            <div className="flex align-items-center">
              <Controller
                control={props.control}
                name="isFeatured"
                render={({ field }) => (
                  <Checkbox
                    inputId="isFeatured"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              <label htmlFor="isFeatured" className="ml-2 cursor-pointer">
                Show on homepage
              </label>
            </div>
          </FormField>
        </div>
      </Panel>
    );
  }

  return (
    <Panel header="Basic Information">
      <div className="flex flex-column gap-2">
        <FormField
          id="title"
          label="Title"
          required
          error={props.errors.title?.message}
        >
          <InputText
            id="title"
            {...props.register("title")}
            className={`w-full ${props.errors.title ? "p-invalid" : ""}`}
            style={{
              ...formStyles.input,
              ...(props.errors.title ? { borderColor: "#dc3545", borderWidth: "1px", borderStyle: "solid" } : {}),
            }}
          />
        </FormField>

        <FormField
          id="description"
          label="Description"
          required
          error={props.errors.description?.message}
        >
          <InputTextarea
            id="description"
            {...props.register("description")}
            rows={5}
            className={`w-full ${props.errors.description ? "p-invalid" : ""}`}
            style={{
              ...formStyles.input,
              ...(props.errors.description ? { borderColor: "#dc3545", borderWidth: "1px", borderStyle: "solid" } : {}),
            }}
          />
        </FormField>

        <FormField
          id="location"
          label="Location"
          optional
          helperText="Location of the event (e.g., Clubhouse, Main Dock, Training Room)"
          error={props.errors.location?.message}
        >
          <InputText
            id="location"
            {...props.register("location")}
            className={`w-full ${props.errors.location ? "p-invalid" : ""}`}
            placeholder="e.g., Clubhouse"
            style={formStyles.input}
          />
        </FormField>

        <FormField
          id="startsAt"
          label="Starts at"
          required
          error={props.errors.startsAt?.message}
        >
          <Controller
            control={props.control}
            name="startsAt"
            render={({ field }) => (
              <AdminCalendar
                id="startsAt"
                value={field.value}
                onChange={(e) => {
                  if (e.value instanceof Date) {
                    field.onChange(e.value);
                    // For non-bookable, non-recurring events, automatically set end date to 1 hour after start
                    if (!props.isBookable && !isRecurring) {
                      const newEndDate = new Date(e.value.getTime() + 60 * 60 * 1000);
                      props.setValue("endsAt", newEndDate, { shouldValidate: true });
                    }
                  }
                }}
                showTime
                showIcon
                className={`w-full ${props.errors.startsAt ? "p-invalid" : ""}`}
                inputStyle={formStyles.input}
                inputClassName={`w-full ${props.errors.startsAt ? "p-invalid" : ""}`}
              />
            )}
          />
        </FormField>

        <FormField
          id="endsAt"
          label="Ends at"
          required
          error={props.errors.endsAt?.message}
        >
          <Controller
            control={props.control}
            name="endsAt"
            render={({ field }) => (
              <AdminCalendar
                id="endsAt"
                value={field.value}
                onChange={(e) => {
                  if (e.value instanceof Date) field.onChange(e.value);
                }}
                showTime
                showIcon
                className={`w-full ${props.errors.endsAt ? "p-invalid" : ""}`}
                inputStyle={formStyles.input}
                inputClassName={`w-full ${props.errors.endsAt ? "p-invalid" : ""}`}
              />
            )}
          />
        </FormField>
      </div>
    </Panel>
  );
}
